import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Search, Upload, Download, Plus, Users, UserX, FileX, AlertTriangle,
  CheckCircle, Calendar, Building2, RefreshCw, X, Edit, Trash2, Link
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

// Helper function to get policy status badge styling
const getPolicyStatusBadge = (policy) => {
  if (policy.is_expired) {
    return { className: 'bg-red-100 text-red-700', label: 'Expired' };
  }
  if (policy.status === 'active') {
    return { className: 'bg-green-100 text-green-700', label: policy.status };
  }
  return { className: 'bg-slate-100 text-slate-700', label: policy.status };
};

const InsuranceManagement = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await api.get('/insurance/dashboard/stats');
      setDashboardStats(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    }
  }, []);

  const fetchPolicies = useCallback(async (page = 1, searchQuery = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/insurance/policies?${params}`);
      setPolicies(response.data.policies);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total
      });
    } catch (err) {
      toast.error('Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchListData = useCallback(async (listType, page = 1) => {
    setLoading(true);
    try {
      let endpoint = '';
      if (listType === 'insured') endpoint = '/insurance/dashboard/insured-employees';
      else if (listType === 'not-insured') endpoint = '/insurance/dashboard/not-insured-employees';
      else if (listType === 'unassigned') endpoint = '/insurance/dashboard/unassigned-policies';
      
      const response = await api.get(`${endpoint}?page=${page}&limit=20`);
      
      if (listType === 'unassigned') {
        setPolicies(response.data.policies);
      } else {
        setEmployees(response.data.employees);
      }
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total
      });
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  useEffect(() => {
    if (activeTab === 'policies') {
      fetchPolicies(1, search);
    } else if (activeTab === 'insured' || activeTab === 'not-insured') {
      fetchListData(activeTab, 1);
    } else if (activeTab === 'unassigned') {
      fetchListData('unassigned', 1);
    }
  }, [activeTab, fetchPolicies, fetchListData, search]);

  const handleSearch = () => {
    if (activeTab === 'policies') {
      fetchPolicies(1, search);
    }
  };

  const handleDeleteAllPolicies = async () => {
    setDeletingAll(true);
    try {
      const response = await api.delete('/insurance/policies/delete-all?confirm=true');
      toast.success(response.data.message || 'All insurance policies deleted successfully');
      setShowDeleteAllModal(false);
      // Refresh data
      fetchDashboardStats();
      fetchPolicies(1, '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete policies');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/insurance/export/${type}`);
      const { data, filename } = response.data;
      
      // Convert to CSV
      if (data.length === 0) {
        toast.info('No data to export');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      await api.delete(`/insurance/policies/${policyId}`);
      toast.success('Policy deleted');
      fetchPolicies(pagination.page, search);
      fetchDashboardStats();
    } catch (err) {
      toast.error('Failed to delete policy');
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, onClick, testId }) => (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg ${onClick ? 'hover:scale-[1.02]' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="insurance-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Insurance Management</h1>
            <p className="text-sm text-slate-500">Manage employee insurance policies</p>
          </div>
        </div>
        
        {isAdmin() && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowDeleteAllModal(true)} 
              data-testid="delete-all-policies-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All
            </Button>
            <Button variant="outline" onClick={() => setShowUploadModal(true)} data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => setShowAddModal(true)} data-testid="add-policy-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Policy
            </Button>
          </div>
        )}
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Users}
            label="Employees Insured"
            value={dashboardStats.employees_insured}
            color="bg-green-500"
            onClick={() => setActiveTab('insured')}
            testId="stat-insured"
          />
          <StatCard
            icon={UserX}
            label="Not Insured"
            value={dashboardStats.employees_not_insured}
            color="bg-red-500"
            onClick={() => setActiveTab('not-insured')}
            testId="stat-not-insured"
          />
          <StatCard
            icon={FileX}
            label="Unassigned Policies"
            value={dashboardStats.unassigned_policies}
            color="bg-orange-500"
            onClick={() => setActiveTab('unassigned')}
            testId="stat-unassigned"
          />
          <StatCard
            icon={dashboardStats.policies_expiring_soon > 0 ? AlertTriangle : CheckCircle}
            label={dashboardStats.policies_expiring_soon > 0 ? "Expiring Within 1 Month" : "Active (Not Expiring Soon)"}
            value={dashboardStats.policies_expiring_soon > 0 ? dashboardStats.policies_expiring_soon : dashboardStats.active_not_expiring}
            color={dashboardStats.policies_expiring_soon > 0 ? "bg-yellow-500" : "bg-blue-500"}
            testId="stat-expiring"
          />
        </div>
      )}

      {/* Expiring Soon Alert */}
      {dashboardStats?.policies_expiring_soon > 0 && dashboardStats.expiring_policies?.length > 0 && (
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="w-5 h-5" />
              Policies Expiring Within 1 Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboardStats.expiring_policies.slice(0, 5).map(policy => (
                <div key={policy.id} className="flex items-center justify-between p-2 bg-white rounded border">
                  <div>
                    <span className="font-medium">{policy.policy_number}</span>
                    <span className="text-slate-500 mx-2">•</span>
                    {policy.employee_id ? (
                      <span 
                        className="cursor-pointer hover:text-blue-600 hover:underline"
                        onClick={() => navigate(`/employee-master/${policy.employee_id}`)}
                      >
                        {policy.employee_name}
                      </span>
                    ) : (
                      <span className="text-orange-500">{policy.employee_name || 'Unassigned'}</span>
                    )}
                  </div>
                  <div className="text-sm text-yellow-700">
                    Expires: {policy.end_date ? formatDate(policy.end_date) : '-'} ({policy.days_until_expiry} days)
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="policies">All Policies</TabsTrigger>
          <TabsTrigger value="insured">Insured Employees</TabsTrigger>
          <TabsTrigger value="not-insured">Not Insured</TabsTrigger>
          <TabsTrigger value="unassigned">Unassigned Policies</TabsTrigger>
        </TabsList>

        {/* All Policies Tab */}
        <TabsContent value="policies">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Insurance Policies</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by Health Card #, Employee..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10 w-72"
                      data-testid="search-policies"
                    />
                  </div>
                  <Button variant="outline" onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : policies.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No policies found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Health Card #</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Type</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Provider</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Employee</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Coverage</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Valid Until</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                        {isAdmin() && <th className="text-left p-3 text-sm font-medium text-slate-600">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {policies.map(policy => (
                        <tr key={policy.id} className="border-b hover:bg-slate-50" data-testid={`policy-row-${policy.id}`}>
                          <td className="p-3 font-medium">{policy.policy_number}</td>
                          <td className="p-3 capitalize">{policy.policy_type}</td>
                          <td className="p-3">{policy.provider_name}</td>
                          <td className="p-3">
                            {policy.employee_name ? (
                              <div 
                                className="cursor-pointer hover:text-blue-600 transition-colors"
                                onClick={() => policy.employee_id && navigate(`/employee-master/${policy.employee_id}`)}
                                data-testid={`policy-employee-link-${policy.id}`}
                              >
                                <div className="font-medium hover:underline">{policy.employee_name}</div>
                                <div className="text-xs text-slate-500">{policy.employee_code}</div>
                              </div>
                            ) : (
                              <span className="text-orange-500">Unassigned</span>
                            )}
                          </td>
                          <td className="p-3">₹{policy.coverage_amount?.toLocaleString()}</td>
                          <td className="p-3">
                            <div>{policy.end_date ? formatDate(policy.end_date) : '-'}</div>
                            {policy.days_until_expiry !== null && policy.days_until_expiry <= 30 && policy.days_until_expiry >= 0 && (
                              <div className="text-xs text-yellow-600">{policy.days_until_expiry} days left</div>
                            )}
                          </td>
                          <td className="p-3">
                            {(() => {
                              const badge = getPolicyStatusBadge(policy);
                              return (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </td>
                          {isAdmin() && (
                            <td className="p-3">
                              <div className="flex gap-1">
                                {!policy.employee_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedPolicy(policy); setShowMapModal(true); }}
                                    title="Map to Employee"
                                  >
                                    <Link className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setSelectedPolicy(policy); setShowAddModal(true); }}
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePolicy(policy.id)}
                                  title="Delete"
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-slate-500">
                    Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => fetchPolicies(pagination.page - 1, search)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.pages}
                      onClick={() => fetchPolicies(pagination.page + 1, search)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insured Employees Tab */}
        <TabsContent value="insured">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Employees with Valid Insurance</CardTitle>
                {isAdmin() && (
                  <Button variant="outline" onClick={() => handleExport('insured')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : employees.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No insured employees found</div>
              ) : (
                <div className="space-y-3">
                  {employees.map(emp => (
                    <div 
                      key={emp.employee_id} 
                      className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/employee-master/${emp.employee_id}`)}
                      data-testid={`insured-emp-${emp.employee_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium hover:text-blue-600">{emp.employee_name}</div>
                          <div className="text-sm text-slate-500">{emp.employee_code}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-green-600">{emp.policy_count} Active Policies</div>
                          <div className="text-xs text-slate-500">
                            {emp.policies?.map(p => p.policy_type).join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Not Insured Employees Tab */}
        <TabsContent value="not-insured">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Employees Without Insurance</CardTitle>
                {isAdmin() && (
                  <Button variant="outline" onClick={() => handleExport('not-insured')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : employees.length === 0 ? (
                <div className="text-center py-8 text-green-600">All employees are insured!</div>
              ) : (
                <div className="space-y-3">
                  {employees.map(emp => (
                    <div 
                      key={emp.employee_id} 
                      className="p-4 border rounded-lg hover:bg-slate-50 border-red-100 cursor-pointer"
                      onClick={() => navigate(`/employee-master/${emp.employee_id}`)}
                      data-testid={`not-insured-emp-${emp.employee_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium hover:text-blue-600">{emp.employee_name}</div>
                          <div className="text-sm text-slate-500">{emp.employee_code} • {emp.email}</div>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          No Insurance
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unassigned Policies Tab */}
        <TabsContent value="unassigned">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Unassigned Policies</CardTitle>
                {isAdmin() && (
                  <Button variant="outline" onClick={() => handleExport('unassigned')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export to Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : policies.length === 0 ? (
                <div className="text-center py-8 text-green-600">All policies are assigned!</div>
              ) : (
                <div className="space-y-3">
                  {policies.map(policy => (
                    <div key={policy.id} className="p-4 border rounded-lg hover:bg-slate-50 border-orange-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{policy.policy_number}</div>
                          <div className="text-sm text-slate-500">
                            {policy.provider_name} • {policy.plan_name} • {policy.policy_type}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right text-sm">
                            <div>Coverage: ₹{policy.coverage_amount?.toLocaleString()}</div>
                            <div className="text-slate-500">Until: {policy.end_date ? formatDate(policy.end_date) : '-'}</div>
                          </div>
                          {isAdmin() && (
                            <Button
                              size="sm"
                              onClick={() => { setSelectedPolicy(policy); setShowMapModal(true); }}
                            >
                              <Link className="w-4 h-4 mr-1" />
                              Assign
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <CardTitle>Insurance Overview</CardTitle>
              <CardDescription>Click on the stats above to view detailed lists</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-slate-500">
                <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p>Select a tab or click on a stat card to view details</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Policy Modal */}
      <AddPolicyModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setSelectedPolicy(null); }}
        policy={selectedPolicy}
        onSave={() => {
          fetchPolicies(pagination.page, search);
          fetchDashboardStats();
        }}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          fetchPolicies(1, '');
          fetchDashboardStats();
        }}
      />

      {/* Map Policy Modal */}
      <MapPolicyModal
        isOpen={showMapModal}
        onClose={() => { setShowMapModal(false); setSelectedPolicy(null); }}
        policy={selectedPolicy}
        onSave={() => {
          fetchPolicies(pagination.page, search);
          fetchDashboardStats();
        }}
      />

      {/* Delete All Confirmation Modal */}
      <Dialog open={showDeleteAllModal} onOpenChange={setShowDeleteAllModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete All Insurance Policies
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-red-800 font-medium">Warning: This action cannot be undone!</p>
              <p className="text-red-700 text-sm mt-2">
                This will permanently delete ALL insurance policies from the system, including:
              </p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                <li>All health insurance policies</li>
                <li>All life insurance policies</li>
                <li>All assigned and unassigned policies</li>
              </ul>
            </div>
            <p className="text-slate-600 text-sm">
              Total policies to be deleted: <strong className="text-red-600">{dashboardStats?.total_policies || 0}</strong>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteAllModal(false)} disabled={deletingAll}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAllPolicies}
              disabled={deletingAll}
              data-testid="confirm-delete-all-btn"
            >
              {deletingAll ? 'Deleting...' : 'Delete All Policies'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Add/Edit Policy Modal Component
const AddPolicyModal = ({ isOpen, onClose, policy, onSave }) => {
  const [form, setForm] = useState({
    policy_number: '',
    policy_type: 'health',
    provider_name: '',
    plan_name: '',
    coverage_amount: '',
    premium_amount: '',
    start_date: '',
    end_date: '',
    employee_id: '',
    beneficiary_name: '',
    beneficiary_relationship: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (policy) {
      setForm({
        policy_number: policy.policy_number || '',
        policy_type: policy.policy_type || 'health',
        provider_name: policy.provider_name || '',
        plan_name: policy.plan_name || '',
        coverage_amount: policy.coverage_amount || '',
        premium_amount: policy.premium_amount || '',
        start_date: policy.start_date || '',
        end_date: policy.end_date || '',
        employee_id: policy.employee_id || '',
        beneficiary_name: policy.beneficiary_name || '',
        beneficiary_relationship: policy.beneficiary_relationship || '',
        notes: policy.notes || ''
      });
    } else {
      setForm({
        policy_number: '',
        policy_type: 'health',
        provider_name: '',
        plan_name: '',
        coverage_amount: '',
        premium_amount: '',
        start_date: '',
        end_date: '',
        employee_id: '',
        beneficiary_name: '',
        beneficiary_relationship: '',
        notes: ''
      });
    }
  }, [policy, isOpen]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/employees?limit=1000');
        setEmployees(response.data);
      } catch (err) {
        console.error('Failed to fetch employees');
      }
    };
    if (isOpen) fetchEmployees();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...form,
        coverage_amount: parseFloat(form.coverage_amount) || 0,
        premium_amount: parseFloat(form.premium_amount) || 0,
        employee_id: form.employee_id || null
      };

      if (policy) {
        await api.put(`/insurance/policies/${policy.id}`, data);
        toast.success('Policy updated');
      } else {
        await api.post('/insurance/policies', data);
        toast.success('Policy created');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{policy ? 'Edit Policy' : 'Add New Policy'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Policy Number *</Label>
              <Input
                value={form.policy_number}
                onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                required
                disabled={!!policy}
              />
            </div>
            <div>
              <Label>Policy Type *</Label>
              <Select value={form.policy_type} onValueChange={(v) => setForm({ ...form, policy_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="health">Health</SelectItem>
                  <SelectItem value="life">Life</SelectItem>
                  <SelectItem value="accident">Accident</SelectItem>
                  <SelectItem value="dental">Dental</SelectItem>
                  <SelectItem value="vision">Vision</SelectItem>
                  <SelectItem value="disability">Disability</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provider Name *</Label>
              <Input
                value={form.provider_name}
                onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Plan Name *</Label>
              <Input
                value={form.plan_name}
                onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Coverage Amount (₹)</Label>
              <Input
                type="number"
                value={form.coverage_amount}
                onChange={(e) => setForm({ ...form, coverage_amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Premium Amount (₹)</Label>
              <Input
                type="number"
                value={form.premium_amount}
                onChange={(e) => setForm({ ...form, premium_amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>End Date *</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required
              />
            </div>
            <div className="col-span-2">
              <Label>Assign to Employee</Label>
              <Select value={form.employee_id || '__unassigned__'} onValueChange={(v) => setForm({ ...form, employee_id: v === '__unassigned__' ? '' : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Beneficiary Name</Label>
              <Input
                value={form.beneficiary_name}
                onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Beneficiary Relationship</Label>
              <Input
                value={form.beneficiary_relationship}
                onChange={(e) => setForm({ ...form, beneficiary_relationship: e.target.value })}
                placeholder="e.g., Spouse, Child"
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (policy ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Bulk Upload Modal with Excel/CSV support
const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'results'

  const downloadTemplate = () => {
    // Health Serve Card format as per user requirements
    const headers = [
      'Employee ID',
      'Employee Name',
      'Date of Birth',
      'Health Serve Card Number',
      'Policy No',
      'Valid From',
      'Valid Till'
    ];
    
    const sampleData = [
      ['EMP001', 'John Smith', '1990-05-15', 'HSC-2025-001', 'POL-001', '2025-01-01', '2025-12-31'],
      ['EMP002', 'Sarah Johnson', '1985-08-22', 'HSC-2025-002', 'POL-002', '2025-01-01', '2025-12-31'],
      ['EMP003', 'Mike Wilson', '1992-03-10', 'HSC-2025-003', 'POL-003', '2025-01-01', '2025-12-31']
    ];
    
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'health_serve_card_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const parseFile = async (selectedFile) => {
    try {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      let policies = [];
      
      if (fileExt === 'csv') {
        const text = await selectedFile.text();
        const lines = text.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_').replace(/^"|"$/g, ''));
        
        policies = lines.slice(1).map(line => {
          // Handle quoted CSV properly
          const values = [];
          let current = '';
          let inQuotes = false;
          for (let char of line) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim().replace(/^"|"$/g, ''));
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim().replace(/^"|"$/g, ''));
          
          const obj = {};
          headers.forEach((h, i) => { obj[h] = values[i] || ''; });
          return obj;
        });
      } else if (fileExt === 'xlsx' || fileExt === 'xls') {
        const XLSX = await import('xlsx');
        const data = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(data);
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        
        // Normalize column names
        policies = jsonData.map(row => {
          const normalized = {};
          Object.keys(row).forEach(key => {
            const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
            normalized[normalizedKey] = String(row[key] || '');
          });
          return normalized;
        });
      } else {
        throw new Error('Unsupported file type. Please use CSV or Excel files.');
      }
      
      // Filter records that have a valid identifier (health_serve_card_number or policy_number)
      return policies.filter(p => 
        p.health_serve_card_number || 
        p.health_card_number || 
        p.card_number || 
        p.policy_number || 
        p.policy_no
      );
    } catch (err) {
      throw new Error(`Failed to parse file: ${err.message}`);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setResults(null);
    setDuplicateReport(null);
    
    try {
      const policies = await parseFile(selectedFile);
      setPreviewData(policies);
      
      // Check for duplicates
      setStep('checking');
      await checkDuplicates(policies);
      
      setStep('preview');
    } catch (err) {
      toast.error(err.message);
      setFile(null);
      setStep('upload');
    }
  };

  const [duplicateReport, setDuplicateReport] = useState(null);

  const checkDuplicates = async (policies) => {
    // Extract health card numbers from policies
    const policyNumbers = policies.map(p => 
      p.health_serve_card_number || 
      p.health_card_number || 
      p.card_number || 
      p.policy_number || 
      ''
    ).filter(Boolean);

    if (policyNumbers.length === 0) return;

    try {
      const response = await api.post('/insurance/policies/check-duplicates', policyNumbers);
      setDuplicateReport(response.data);
    } catch (err) {
      console.error('Error checking duplicates:', err);
      // Don't block upload if duplicate check fails
      setDuplicateReport(null);
    }
  };

  const handleUpload = async () => {
    if (!previewData || previewData.length === 0) return;
    setLoading(true);

    const CHUNK_SIZE = 200; // Upload 200 records at a time
    const totalRecords = previewData.length;
    const totalChunks = Math.ceil(totalRecords / CHUNK_SIZE);
    
    let totalSuccess = 0;
    let totalFailed = 0;
    let totalSkipped = 0;
    let allErrors = [];
    
    try {
      setStep('uploading'); // New step for progress tracking
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalRecords);
        const chunk = previewData.slice(start, end);
        
        // Update progress
        setUploadProgress({
          current: i + 1,
          total: totalChunks,
          processed: start,
          totalRecords: totalRecords
        });
        
        try {
          const response = await api.post('/insurance/policies/bulk-upload', chunk);
          totalSuccess += response.data.success || 0;
          totalFailed += response.data.failed || 0;
          totalSkipped += response.data.skipped || 0;
          
          if (response.data.errors?.length > 0) {
            // Adjust error row numbers for this chunk
            const adjustedErrors = response.data.errors.map(err => {
              const match = err.match(/^Row (\d+):/);
              if (match) {
                const rowNum = parseInt(match[1]) + start - 1;
                return err.replace(/^Row \d+:/, `Row ${rowNum}:`);
              }
              return err;
            });
            allErrors = [...allErrors, ...adjustedErrors];
          }
        } catch (chunkErr) {
          // If a chunk fails, log it but continue with other chunks
          const errorMsg = chunkErr.response?.data?.detail || 'Chunk upload failed';
          allErrors.push(`Chunk ${i + 1} (rows ${start + 2}-${end + 1}): ${errorMsg}`);
          totalFailed += chunk.length;
        }
        
        // Small delay between chunks to avoid overwhelming the server
        if (i < totalChunks - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Set final results
      setResults({
        success: totalSuccess,
        failed: totalFailed,
        skipped: totalSkipped,
        errors: allErrors.slice(0, 50) // Limit errors shown to 50
      });
      setStep('results');
      
      if (totalSuccess > 0) {
        toast.success(`Uploaded ${totalSuccess.toLocaleString()} health cards successfully`);
        onSuccess();
      }
      if (totalFailed > 0) {
        toast.warning(`${totalFailed.toLocaleString()} health cards failed to upload`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setResults({
        success: totalSuccess,
        failed: totalFailed + (previewData.length - totalSuccess - totalFailed),
        skipped: totalSkipped,
        errors: [...allErrors, err.message || 'Upload process failed']
      });
      setStep('results');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const [uploadProgress, setUploadProgress] = useState(null);

  const resetModal = () => {
    setFile(null);
    setResults(null);
    setPreviewData(null);
    setDuplicateReport(null);
    setUploadProgress(null);
    setStep('upload');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Bulk Upload Health Serve Cards
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {step === 'upload' && (
            <>
              {/* Required Columns Table */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-3">Required Excel Columns</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-blue-900 text-white">
                        <th className="px-3 py-2 text-left font-medium">Employee ID</th>
                        <th className="px-3 py-2 text-left font-medium">Employee Name</th>
                        <th className="px-3 py-2 text-left font-medium">Date of Birth</th>
                        <th className="px-3 py-2 text-left font-medium">Health Serve Card Number</th>
                        <th className="px-3 py-2 text-left font-medium">Policy No</th>
                        <th className="px-3 py-2 text-left font-medium">Valid From</th>
                        <th className="px-3 py-2 text-left font-medium">Valid Till</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white border-b">
                        <td className="px-3 py-2 text-slate-600 text-xs">EMP001</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">John Smith</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">1990-05-15</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">HSC-2025-001</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">POL-001</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">2025-01-01</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">2025-12-31</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <ul className="mt-3 text-xs text-blue-700 space-y-1">
                  <li>• <strong>Health Serve Card Number</strong> is required and must be unique</li>
                  <li>• Date format: YYYY-MM-DD (e.g., 2025-01-01)</li>
                  <li>• Supported formats: <strong>Excel (.xlsx, .xls)</strong> and <strong>CSV</strong></li>
                </ul>
              </div>
              
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Need a template?</p>
                  <p className="text-sm text-slate-500">Download a pre-formatted template with sample data</p>
                </div>
                <Button variant="outline" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
              
              {/* File Input */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors">
                <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                <p className="mb-2">Drag and drop your Excel file here, or click to browse</p>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="max-w-xs mx-auto"
                />
              </div>
            </>
          )}
          
          {/* Checking for Duplicates Step */}
          {step === 'checking' && (
            <div className="py-12 text-center">
              <RefreshCw className="w-10 h-10 mx-auto mb-4 text-blue-600 animate-spin" />
              <h4 className="text-lg font-semibold text-slate-800 mb-2">Checking for Duplicates...</h4>
              <p className="text-slate-500">Scanning {previewData?.length?.toLocaleString() || 0} health cards</p>
            </div>
          )}

          {step === 'preview' && previewData && (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Preview: {previewData.length.toLocaleString()} health cards found</h4>
                  <p className="text-sm text-slate-500">{file?.name}</p>
                </div>
                <Button variant="outline" size="sm" onClick={resetModal}>
                  Choose Different File
                </Button>
              </div>

              {/* Duplicate Detection Report */}
              {duplicateReport && (
                <div className={`p-4 rounded-lg border ${
                  duplicateReport.duplicate_count > 0 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {duplicateReport.duplicate_count > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h5 className={`font-medium ${
                        duplicateReport.duplicate_count > 0 ? 'text-amber-800' : 'text-green-800'
                      }`}>
                        Duplicate Detection Report
                      </h5>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-green-500"></span>
                          <span className="text-slate-700">
                            <strong>{duplicateReport.new_count.toLocaleString()}</strong> new health cards to upload
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                          <span className="text-slate-700">
                            <strong>{duplicateReport.duplicate_count.toLocaleString()}</strong> duplicates will be skipped
                          </span>
                        </div>
                      </div>
                      
                      {/* Show duplicate details if any */}
                      {duplicateReport.duplicate_count > 0 && duplicateReport.duplicate_details?.length > 0 && (
                        <details className="mt-3">
                          <summary className="text-sm text-amber-700 cursor-pointer hover:text-amber-800 font-medium">
                            View duplicate health card numbers ({Math.min(duplicateReport.duplicate_details.length, 20)} shown)
                          </summary>
                          <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded border border-amber-200 p-2">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-slate-500">
                                  <th className="p-1">Health Card #</th>
                                  <th className="p-1">Employee</th>
                                  <th className="p-1">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {duplicateReport.duplicate_details.slice(0, 20).map((dup, idx) => (
                                  <tr key={`dup-${idx}`} className="border-t border-slate-100">
                                    <td className="p-1 font-mono text-amber-700">{dup.policy_number}</td>
                                    <td className="p-1">{dup.employee_name || 'Unassigned'}</td>
                                    <td className="p-1">
                                      <Badge variant="outline" className="text-xs capitalize">
                                        {dup.status}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {duplicateReport.duplicate_details.length > 20 && (
                              <p className="text-xs text-slate-500 mt-2 text-center">
                                ...and {duplicateReport.duplicate_details.length - 20} more duplicates
                              </p>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Preview Table */}
              <div className="border rounded-lg overflow-x-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">Employee ID</th>
                      <th className="p-2 text-left font-medium">Employee Name</th>
                      <th className="p-2 text-left font-medium">DOB</th>
                      <th className="p-2 text-left font-medium">Health Card #</th>
                      <th className="p-2 text-left font-medium">Policy No</th>
                      <th className="p-2 text-left font-medium">Valid From</th>
                      <th className="p-2 text-left font-medium">Valid Till</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 10).map((policy, idx) => {
                      // Support both formats - normalized keys
                      const empId = policy.employee_id || policy.emp_id || policy.empid || '';
                      const empName = policy.employee_name || policy.emp_name || policy.name || '';
                      const dob = policy.date_of_birth || policy.dob || policy.birth_date || '';
                      const healthCard = policy.health_serve_card_number || policy.health_card_number || policy.card_number || policy.policy_number || '';
                      const policyNo = policy.policy_no || (policy.health_serve_card_number ? '' : policy.policy_number) || '';
                      const validFrom = policy.valid_from || policy.start_date || policy.from_date || '';
                      const validTill = policy.valid_till || policy.valid_to || policy.end_date || policy.to_date || '';
                      
                      return (
                        <tr key={`preview-${healthCard}-${idx}`} className="border-t hover:bg-slate-50">
                          <td className="p-2 font-mono text-xs">{empId || <span className="text-slate-400">-</span>}</td>
                          <td className="p-2 text-sm">{empName || <span className="text-slate-400">-</span>}</td>
                          <td className="p-2 text-xs">{dob || <span className="text-slate-400">-</span>}</td>
                          <td className="p-2 font-mono text-xs font-medium text-blue-700">{healthCard}</td>
                          <td className="p-2 text-xs">{policyNo || <span className="text-slate-400">-</span>}</td>
                          <td className="p-2 text-xs">{validFrom}</td>
                          <td className="p-2 text-xs">{validTill}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {previewData.length > 10 && (
                  <div className="p-2 text-center text-sm text-slate-500 bg-slate-50">
                    ...and {(previewData.length - 10).toLocaleString()} more health cards
                  </div>
                )}
              </div>
            </>
          )}

          {/* Upload Progress Step */}
          {step === 'uploading' && uploadProgress && (
            <div className="space-y-6 py-8">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
                <h4 className="text-lg font-semibold text-slate-800 mb-2">Uploading Health Cards...</h4>
                <p className="text-slate-600">
                  Processing batch {uploadProgress.current} of {uploadProgress.total}
                </p>
              </div>
              
              {/* Progress Bar */}
              <div className="max-w-md mx-auto">
                <div className="flex justify-between text-sm text-slate-600 mb-2">
                  <span>{uploadProgress.processed.toLocaleString()} processed</span>
                  <span>{uploadProgress.totalRecords.toLocaleString()} total</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.processed / uploadProgress.totalRecords) * 100}%` }}
                  />
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  {Math.round((uploadProgress.processed / uploadProgress.totalRecords) * 100)}% complete
                </p>
              </div>
              
              <p className="text-center text-xs text-slate-400">
                Please do not close this window until upload is complete
              </p>
            </div>
          )}
          
          {step === 'results' && results && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-700">{(results.success || 0).toLocaleString()}</p>
                  <p className="text-sm text-green-600">Successfully Uploaded</p>
                </div>
                <div className={`p-4 border rounded-lg text-center ${results.skipped > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                  <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${results.skipped > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                  <p className={`text-2xl font-bold ${results.skipped > 0 ? 'text-amber-700' : 'text-slate-500'}`}>{(results.skipped || 0).toLocaleString()}</p>
                  <p className={`text-sm ${results.skipped > 0 ? 'text-amber-600' : 'text-slate-500'}`}>Skipped (Duplicates)</p>
                </div>
                <div className={`p-4 border rounded-lg text-center ${results.failed > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                  {results.failed > 0 ? (
                    <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
                  ) : (
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  )}
                  <p className={`text-2xl font-bold ${results.failed > 0 ? 'text-red-700' : 'text-slate-500'}`}>{(results.failed || 0).toLocaleString()}</p>
                  <p className={`text-sm ${results.failed > 0 ? 'text-red-600' : 'text-slate-500'}`}>Failed</p>
                </div>
              </div>
              
              {/* Errors List */}
              {results.errors?.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Upload Errors:</h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                    {results.errors.map((error, idx) => (
                      <li key={`error-${idx}`}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            {step === 'results' ? 'Done' : 'Cancel'}
          </Button>
          {step === 'preview' && (
            <Button onClick={handleUpload} disabled={loading || (duplicateReport?.new_count === 0)}>
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : duplicateReport?.new_count === 0 ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  All Cards Already Exist
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {(duplicateReport?.new_count || previewData?.length || 0).toLocaleString()} Health Cards
                  {duplicateReport?.duplicate_count > 0 && (
                    <span className="ml-1 text-xs opacity-75">
                      ({duplicateReport.duplicate_count.toLocaleString()} will be skipped)
                    </span>
                  )}
                </>
              )}
            </Button>
          )}
          {step === 'results' && results?.success > 0 && (
            <Button variant="outline" onClick={resetModal}>
              Upload More
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Map Policy Modal
const MapPolicyModal = ({ isOpen, onClose, policy, onSave }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const params = search ? `?search=${search}&limit=50` : '?limit=50';
        const response = await api.get(`/employees${params}`);
        setEmployees(response.data);
      } catch (err) {
        console.error('Failed to fetch employees');
      }
    };
    if (isOpen) fetchEmployees();
  }, [isOpen, search]);

  const handleMap = async () => {
    if (!employeeId || !policy) return;
    setLoading(true);

    try {
      await api.post(`/insurance/policies/${policy.id}/map`, {
        policy_id: policy.id,
        employee_id: employeeId
      });
      toast.success('Policy mapped to employee');
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to map policy');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !policy) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Policy to Employee</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium">{policy.policy_number}</p>
            <p className="text-sm text-slate-500">{policy.provider_name} - {policy.plan_name}</p>
          </div>
          
          <div>
            <Label>Search Employee</Label>
            <Input
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          <div>
            <Label>Select Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleMap} disabled={!employeeId || loading}>
            {loading ? 'Assigning...' : 'Assign Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InsuranceManagement;
